import { platformIdleTimer } from '@/analytics/platformIdleTimer';
import { sendActivityBatch } from '@/api/activity';

const FLUSH_INTERVAL_MS = 30_000;

export interface ActivityEvent {
  type: string;
  context?: Record<string, unknown>;
  /** Absolute wall-clock ms (`Date.now()`), sent to the backend as-is. */
  timestampMs: number;
}

export type ActivityEntityType = 'item' | 'scene';

/** What a tracked event is about - a single item or scene. Kept as one object rather
 * than parallel itemId/sceneId(+name) props, so shared UI (e.g. Annotation.vue) can
 * stay agnostic to which entity type it's currently showing. */
export interface ActivityEntity {
  type: ActivityEntityType;
  id: string;
  name?: string;
}

/** Expands an `ActivityEntity` into the `itemId`/`sceneId`(+name) context fields the
 * backend's activity queries filter on - only how call sites build that shape changes. */
export const activityEntityContext = (
  entity: ActivityEntity
): Record<string, string | undefined> =>
  entity.type === 'item'
    ? { itemId: entity.id, itemName: entity.name }
    : { sceneId: entity.id, sceneName: entity.name };

export interface ActivityBatchPayload {
  sessionId: string;
  batchIndex: number;
  /** Delta since the last batch, not cumulative. */
  activeMs: number;
  events: Array<{
    type: string;
    context?: Record<string, unknown>;
    /** Absolute epoch ms - the backend writes this straight into `ActivityEvent.created_at`. */
    timestampMs: number;
  }>;
  /** The route's *template*, e.g. `/item/:id/embed` - never the resolved path,
   * so a page type stays directly groupable/queryable on its own. */
  route: string;
  /** Dynamic segment values for `route`, e.g. `{ id: '<uuid>' }` - kept separate
   * so activity for one specific item/scene is queryable by id. */
  routeParams: Record<string, string>;
}

/** Accumulates active-time-on-page and event tracking records for the analytics
 * pipeline. Plain TS class, not a composable, so it survives route changes. */
class ActivityCollector {
  /** Generated fresh per tab, held in memory only. Not `readonly`:
   * `resetSession()` regenerates it on login/logout. */
  sessionId: string = crypto.randomUUID();
  private batchIndex = 0;

  private accumulatedActiveMs = 0;
  private activeSince: number | null = null;
  private events: ActivityEvent[] = [];
  /** Staged events not yet queued for flush - see `stock()`/`commitStock()`/`discardStock()`. */
  private stockedEvents: ActivityEvent[] = [];

  /** Current route's template + params, kept up to date by `setCurrentRoute()`.
   * This is what every trigger *other than* route change flushes for. */
  private currentRoute = window.location.pathname;
  private currentRouteParams: Record<string, string> = {};

  constructor() {
    if (platformIdleTimer.isActive()) {
      this.activeSince = Date.now();
    }

    platformIdleTimer.addEventListener('active', () => {
      this.activeSince = Date.now();
    });
    platformIdleTimer.addEventListener('inactive', () => {
      this.closeActiveWindow();
    });

    // Reliable signal for tab switch, backgrounding, most close paths.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush();
    });

    // Fallback for close paths where visibilitychange doesn't fire 'hidden'.
    window.addEventListener('pagehide', () => this.flush());

    // Best-effort last resort; flush() no-ops if an earlier trigger already fired.
    window.addEventListener('beforeunload', () => this.flush());

    // Bounds data loss from a hard crash/kill with no exit event.
    window.setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
  }

  private closeActiveWindow() {
    if (this.activeSince === null) return;
    this.accumulatedActiveMs += Date.now() - this.activeSince;
    this.activeSince = null;
  }

  /** Reads and resets the active-ms accumulator, closing then reopening the
   * in-progress window so a flush mid-window still counts and calls can't double-count. */
  consumeActiveMs(): number {
    const wasActive = this.activeSince !== null;
    this.closeActiveWindow();
    const activeMs = this.accumulatedActiveMs;
    this.accumulatedActiveMs = 0;
    if (wasActive) {
      this.activeSince = Date.now();
    }
    return activeMs;
  }

  /** Call site entry point for annotation selection, link clicks, etc. */
  trackEvent(type: string, context?: Record<string, unknown>) {
    this.events.push({ type, context, timestampMs: Date.now() });
  }

  /** Reads and resets the event queue so back-to-back calls can't double-send. */
  consumeEvents(): ActivityEvent[] {
    const events = this.events;
    this.events = [];
    return events;
  }

  /** Stages an event without queuing it for flush, for call sites where the
   * outcome (save vs. cancel) isn't known yet. Timestamps immediately, same as
   * `trackEvent()`, so the real time survives however long it sits staged. */
  stock(type: string, context?: Record<string, unknown>) {
    this.stockedEvents.push({ type, context, timestampMs: Date.now() });
  }

  /** Moves every staged event into the real flush queue. Only called after a
   * successful save, so `success: true` is stamped in here. */
  commitStock() {
    this.events.push(
      ...this.stockedEvents.map((event) => ({
        ...event,
        context: { ...event.context, success: true },
      }))
    );
    this.stockedEvents = [];
  }

  /** Drops every staged event without ever queuing it for flush. */
  discardStock() {
    this.stockedEvents = [];
  }

  /** Current batch's index, then increments - lets the backend detect gaps from dropped batches. */
  nextBatchIndex(): number {
    return this.batchIndex++;
  }

  /** Updates the route every non-route-change trigger flushes for. Called
   * from `router.afterEach` with the route being entered, right after
   * flushing the route being left (see `routeOverride` below). */
  setCurrentRoute(route: string, routeParams: Record<string, string>) {
    this.currentRoute = route;
    this.currentRouteParams = routeParams;
  }

  /** Drains the accumulator + event queue and sends a batch. `routeOverride`
   * is for the route-change trigger, to attribute the delta to the page being
   * left rather than whatever `setCurrentRoute()` has since updated to. */
  flush(routeOverride?: string, routeParamsOverride?: Record<string, string>) {
    const activeMs = this.consumeActiveMs();
    const events = this.consumeEvents().map((event) => ({
      type: event.type,
      context: event.context,
      timestampMs: event.timestampMs,
    }));

    if (activeMs === 0 && events.length === 0) return;

    const payload: ActivityBatchPayload = {
      sessionId: this.sessionId,
      batchIndex: this.nextBatchIndex(),
      activeMs,
      events,
      route: routeOverride ?? this.currentRoute,
      routeParams: routeParamsOverride ?? this.currentRouteParams,
    };

    void sendActivityBatch(payload);
  }

  /** Call on login/logout: flushes the outgoing identity's delta, then starts a
   * fresh session so it never straddles two users. MUST be called before the
   * auth token itself changes, so the flush attributes to the outgoing identity. */
  resetSession() {
    this.flush();
    this.discardStock(); // Prevent staged events from leaking into the new session
    this.sessionId = crypto.randomUUID();
    this.batchIndex = 0;
  }
}

/** Singleton - one active-time/event stream per tab, not one per component. */
export const activityCollector = new ActivityCollector();
